import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { primaryBtn } from "../../../styles";
import { LeaderboardColumn, LeaderboardCtx, Member } from "./types";
import { getLeaderboardColumns, getPreparingCols, getPreparingAction } from "./columns";
import { memberHref } from "./links";
import { sortMembers } from "./sort";

interface Props {
  ctx: LeaderboardCtx;
  readyMembers: Member[];
  approvedMembers: Member[];
  notReadyMembers: Member[];
  myMember: Member | null;
  onInviteFriends?: () => void;
}

function gridTemplate(cols: LeaderboardColumn[], isMobile: boolean, isStarted: boolean): string {
  return cols
    .map((c) => {
      if (isMobile && isStarted) return c.mobileWidth ?? null;
      return c.width;
    })
    .filter(Boolean)
    .join(" ");
}

function visibleCols(cols: LeaderboardColumn[], isMobile: boolean, isStarted: boolean): LeaderboardColumn[] {
  if (!isMobile || !isStarted) return cols;
  return cols.filter((c) => c.mobileWidth !== undefined);
}

const headerCell: React.CSSProperties = {
  fontSize: "0.6rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: "var(--color-text-dim)",
};

export default function Leaderboard({ ctx, readyMembers, approvedMembers, notReadyMembers, myMember, onInviteFriends }: Props) {
  const { t } = useTranslation();
  const { mode, isStarted, isCompleted, isMobile, myMemberId } = ctx;

  const allCols = getLeaderboardColumns(ctx, t);
  const cols = visibleCols(allCols, isMobile, isStarted);
  const colsStr = gridTemplate(allCols, isMobile, isStarted);

  const sorted = isStarted
    ? sortMembers(readyMembers, mode, ctx.tiebreakerGoalsToDate)
    : approvedMembers;

  const myMemberInList = myMember
    ? sorted.some((m) => m.id === myMember.id) || notReadyMembers.some((m) => m.id === myMember.id)
    : true;

  const prepCols = getPreparingCols(mode);

  function renderRow(m: Member, rankStr: string, i: number, total: number, isNr = false) {
    const isMe = m.id === myMemberId;
    const rankNum = parseInt(rankStr, 10);
    const isWinner = rankNum === 1 && isCompleted;
    const isLast = i === total - 1;
    return (
      <div
        key={m.id}
        style={{
          display: "grid",
          gridTemplateColumns: colsStr,
          columnGap: "0.5rem",
          padding: isMobile ? "0.5rem 0.75rem" : "0.75rem 1.25rem",
          alignItems: "center",
          borderBottom: !isLast ? "1px solid var(--color-border-subtle)" : "none",
          background: isNr ? "transparent" : isWinner ? "oklch(0.72 0.16 80 / 0.05)" : isMe ? "var(--color-accent-glow)" : "transparent",
          opacity: isNr ? 0.55 : 1,
        }}
      >
        {cols.map((col) => (
          <React.Fragment key={col.key}>
            {col.renderCell(m, ctx, isNr)}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Column headers */}
      {isStarted ? (
        <div style={{ display: "grid", gridTemplateColumns: colsStr, columnGap: "0.5rem", padding: isMobile ? "0.5rem 0.75rem" : "0.5rem 1.25rem", borderBottom: "1px solid var(--color-border)" }}>
          {cols.map((col) => (
            <span key={col.key} style={{ ...headerCell, textAlign: col.align === "center" ? "center" : col.align === "right" ? "right" : undefined }}>
              {col.header ?? ""}
            </span>
          ))}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: prepCols.gridTemplate, columnGap: "0.5rem", padding: isMobile ? "0.5rem 0.75rem" : "0.5rem 1.25rem", borderBottom: "1px solid var(--color-border)" }}>
          <span style={headerCell}>{t("league.leaderboard.player")}</span>
          {prepCols.showReady && <span style={{ ...headerCell, textAlign: "right" }}>{t("league.leaderboard.ready")}</span>}
          <span />
        </div>
      )}

      {/* PREPARING rows */}
      {!isStarted && sorted.map((m, i) => {
        const isMe = m.id === myMemberId;
        const action = isMe && myMemberId
          ? getPreparingAction(mode, m.isTeamValid, ctx.leagueId, myMemberId, ctx.gwcNextPickMissing, t)
          : null;
        return (
          <div
            key={m.id}
            style={{
              display: "grid",
              gridTemplateColumns: prepCols.gridTemplate,
              columnGap: "0.5rem",
              padding: isMobile ? "0.5rem 0.75rem" : "0.75rem 1.25rem",
              alignItems: "center",
              borderBottom: i < sorted.length - 1 ? "1px solid var(--color-border-subtle)" : "none",
              background: isMe ? "var(--color-accent-glow)" : "transparent",
            }}
          >
            <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <Link
                to={memberHref(m, ctx)}
                style={{ fontSize: "0.88rem", fontWeight: isMe ? 600 : 400, color: isMe ? "var(--color-accent)" : "var(--color-text)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {m.displayName}
              </Link>
              {m.userId === null && (
                <span style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-dim)", flexShrink: 0 }}>{t("league.leaderboard.guestBadge")}</span>
              )}
            </div>
            {prepCols.showReady && (
              <div style={{ textAlign: "right" }}>
                {m.isTeamValid
                  ? <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", padding: "2px 7px", borderRadius: 99, background: "color-mix(in srgb, var(--color-success) 15%, transparent)", border: "1px solid var(--color-success)", color: "var(--color-success)" }}>{t("league.leaderboard.yes")}</span>
                  : <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", padding: "2px 7px", borderRadius: 99, background: "var(--color-surface-alt)", border: "1px solid var(--color-border)", color: "var(--color-text-dim)" }}>{t("league.leaderboard.no")}</span>
                }
              </div>
            )}
            <div style={{ textAlign: "right" }}>
              {action && (
                <Link to={action.href} style={{ ...primaryBtn, textDecoration: "none", fontSize: "0.72rem", padding: "0.25rem 0.6rem", display: "inline-block" }}>
                  {action.label}
                </Link>
              )}
            </div>
          </div>
        );
      })}

      {/* PREPARING: invite friends CTA */}
      {!isStarted && onInviteFriends && (
        <button
          onClick={onInviteFriends}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem",
            width: "100%", padding: isMobile ? "0.65rem 0.75rem" : "0.75rem 1.25rem",
            background: "none", border: "none", borderTop: "1px solid var(--color-border-subtle)",
            cursor: "pointer", fontSize: "0.85rem", color: "var(--color-accent)", fontWeight: 600,
            fontFamily: "var(--font-body)",
          }}
        >
          + {t("league.howItWorks.inviteFriends")}
        </button>
      )}

      {/* RUNNING / COMPLETED: ranked rows */}
      {isStarted && sorted.map((m, i) => renderRow(m, ctx.ranks.get(m.id) ?? "—", i, sorted.length))}

      {/* Not-ready members */}
      {isStarted && notReadyMembers.length > 0 && (
        <>
          <div style={{ padding: "0.35rem 1.25rem", background: "var(--color-surface-alt)", borderTop: "1px solid var(--color-border-subtle)", borderBottom: "1px solid var(--color-border-subtle)" }}>
            <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)" }}>{t("league.leaderboard.didNotSubmit")}</span>
          </div>
          {notReadyMembers.map((m, i) => renderRow(m, "—", i, notReadyMembers.length, true))}
        </>
      )}

      {/* Pinned: current user if not in ranked or not-ready list */}
      {isStarted && !myMemberInList && myMember && (
        <div style={{ borderTop: "1px solid var(--color-border)", background: "var(--color-accent-glow)" }}>
          {renderRow(myMember, ctx.ranks.get(myMember.id) ?? "—", 0, 1)}
        </div>
      )}
    </>
  );
}
